import axios from 'axios';
import { store } from '../store/store';
import { setToken } from '../store/slices/authSlice';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token and organization override header
api.interceptors.request.use(
  (config) => {
    // Don't add token for public endpoints
    const publicEndpoints = ['/v1/auth/register/', '/v1/auth/login/'];
    const isPublicEndpoint = publicEndpoints.some(endpoint => config.url?.includes(endpoint));

    if (!isPublicEndpoint) {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add organization override header for superusers
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          if (user.is_superuser) {
            const selectedOrgStr = localStorage.getItem('selectedOrganization');
            if (selectedOrgStr) {
              const selectedOrg = JSON.parse(selectedOrgStr);
              config.headers['X-Organization-Id'] = selectedOrg.id;
            } else {
              // No selectedOrg means "All Organizations" mode
              config.headers['X-Organization-Id'] = '';
            }
          }
        }
      } catch (e) {
        // Ignore JSON parse errors
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't retry if already retried, if it's the refresh endpoint itself, or no refresh token
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh/')) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          // Use axios directly for refresh to avoid interceptor loop
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/v1/auth/refresh/`,
            { refresh: refreshToken },
            { headers: { 'Content-Type': 'application/json' } }
          );

          const { access } = response.data;
          localStorage.setItem('token', access);

          // Update Redux store so WebSocket can reconnect with fresh token
          store.dispatch(setToken(access));

          // Update the failed request with new token
          originalRequest.headers.Authorization = `Bearer ${access}`;

          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/auth/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/auth/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;